import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/hooks";
import { selectShops, selectUnreadNotifications } from "@/features/shell/legacySelectors";
import { StatusBadge } from "@/shared/layout";
import type { Notification } from "@/shared/types/notification";
import { Modal } from "@/shared/ui";
import { Bell, CheckCheck, ChevronRight, Inbox, LoaderCircle, RefreshCw, X } from "@/shared/ui/icons";
import { useShellDialogs } from "../dialogs";
import { notificationMeta, notificationPayload, timeAgo } from "./codes";
import { useNotificationList } from "./useNotificationList";
import { useNotificationRedirect } from "./useNotificationRedirect";

export function NotificationsModal() {
  const { t } = useTranslation();
  const dialogs = useShellDialogs();
  const unread = useAppSelector(selectUnreadNotifications);
  const shops = useAppSelector(selectShops);
  const list = useNotificationList(dialogs.notificationsOpen);
  const redirect = useNotificationRedirect();

  const close = () => dialogs.closeNotifications();
  // Una lettura fallita non blocca dettaglio e redirect: il legacy non aspettava readNotification.
  const goToDetail = (notification: Notification) => {
    void list.markRead(notification.id).catch(() => {});
    dialogs.openNotificationDetail({ ...notification });
  };
  const viewIssue = (notification: Notification) => {
    void list.markRead(notification.id).catch(() => {});
    redirect(notification);
    close();
  };
  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const node = event.currentTarget;
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 2) list.loadMore();
  };
  // eslint-disable-next-line react-hooks/purity -- tempo relativo, come nel legacy
  const now = Date.now();

  return (
    <Modal
      id="notifications-modal"
      open={dialogs.notificationsOpen}
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={t("notificationsModal.notificationCentre")}
      className="dw-notifications-dialog"
    >
      <div className="dw-modal-shell">
        <header className="dw-modal-header">
          <div className="dw-modal-heading">
            <h2 className="flex items-center gap-2">
              <Bell aria-hidden="true" />
              {t("notificationsModal.notificationCentre")}
            </h2>
            <p>
              {list.items.length} notifications · {unread} unread
            </p>
          </div>
          <div className="dw-modal-header-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm gap-2"
              onClick={() => void list.refresh()}
              disabled={list.refreshing}
              aria-label={t("notificationsModal.refresh")}
            >
              <RefreshCw className={`size-4 ${list.refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
              {t("notificationsModal.refresh")}
            </button>
            {unread > 0 && (
              <button
                type="button"
                className="btn btn-ghost btn-sm gap-2 text-primary"
                onClick={() => void list.markAllRead().catch(() => {})}
              >
                <CheckCheck className="size-4" aria-hidden="true" />
                {t("notificationsModal.readAll")}
              </button>
            )}
            <button
              type="button"
              className="dw-modal-close btn btn-ghost btn-sm btn-square"
              aria-label="Close"
              onClick={close}
            >
              <X aria-hidden="true" />
            </button>
          </div>
        </header>
        <div className="as-scroll dw-notification-list" onScroll={onScroll}>
          {list.items.map((notification) => {
            const meta = notificationMeta(notification.code);
            const status = meta.status || "neutral";
            const hasDetail =
              Boolean(notification.detail) &&
              notification.detail !== "" &&
              Object.keys(notification.detail as object).length > 0;
            return (
              <article
                className={`dw-notification-item ${!notification.readAt ? "is-unread" : ""} is-${status}`}
                key={notification.id}
              >
                <div className="dw-notification-meta">
                  <span>{timeAgo(now - notification.createdAt * 1000, t)}</span>
                  {!notification.readAt && (
                    <StatusBadge tone="error">{t("notificationsModal.new")}</StatusBadge>
                  )}
                </div>
                <h3>{t(`notificationsModal.${meta.title}`)}</h3>
                <p
                  dangerouslySetInnerHTML={{
                    __html: t(`notificationsModal.${meta.message}`, notificationPayload(notification, shops)),
                  }}
                />
                {(meta.redirect || hasDetail) && (
                  <div className="dw-notification-actions">
                    {meta.redirect && (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => viewIssue(notification)}
                      >
                        {t("notificationsModal.viewIssue")}
                        <ChevronRight className="size-4" aria-hidden="true" />
                      </button>
                    )}
                    {hasDetail && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => goToDetail(notification)}
                      >
                        {t("notificationsModal.detail")}
                      </button>
                    )}
                  </div>
                )}
              </article>
            );
          })}
          {list.loadingMore && (
            <div className="dw-notification-end" role="status">
              <LoaderCircle className="animate-spin" aria-hidden="true" />
              Loading…
            </div>
          )}
          {!list.pageFull && list.items.length > 0 && (
            <div className="dw-notification-end">{t("notificationsModal.endNotifications")}</div>
          )}
          {!list.items.length && (
            <div className="dw-modal-empty">
              <Inbox aria-hidden="true" />
              <strong>{t("notificationsModal.noNotification")}</strong>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
